import { useEffect, useRef, RefObject } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  onLoadMore: () => void;
  threshold?: number;
  rootMargin?: string;
}

/**
 * Custom hook for infinite scroll using Intersection Observer API
 * @param options - Configuration options for infinite scroll
 * @returns A ref callback that should be attached to the loader element
 */
export const useInfiniteScroll = ({
  hasMore,
  onLoadMore,
  threshold = 0.1,
  rootMargin = '100px',
}: UseInfiniteScrollOptions): RefObject<HTMLDivElement> => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    const loaderElement = loaderRef.current;

    if (!loaderElement || !hasMore) {
      // Clean up observer if element doesn't exist or no more pages
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Only trigger if element is intersecting and we're not already loading
        if (entry.isIntersecting && hasMore && !isLoadingRef.current) {
          isLoadingRef.current = true;
          onLoadMore();

          // Reset loading flag after a short delay to prevent rapid firing
          setTimeout(() => {
            isLoadingRef.current = false;
          }, 1000);
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    // Start observing the loader element
    observerRef.current.observe(loaderElement);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isLoadingRef.current = false;
    };
  }, [hasMore, onLoadMore, threshold, rootMargin]);

  return loaderRef;
};
